import clsx from "clsx";
import React, { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { partTypeAccessory } from "../../../constants/partTypes";
import { useLanguage } from "../../../i18n/useLanguage";
import { AccessoryPartItem } from "../../../queries/useSearchAccessoriesByVehicle";
import { Image } from "../../../ui/Image/Image";
import { KeyValue } from "../../../ui/KeyValue/KeyValue";
import { PriceDisplay } from "../../../ui/PriceDisplay/PriceDisplay";
import { Section } from "../../../ui/Section/Section";
import { getPartFallbackSrc } from "../../../util/getPartFallbackSrc";
import {
  PricingOption,
  getFlatPricingOptions,
} from "../../PriceSelect/getFlatPricingOptions";
import { SectionHeading } from "../Common/SectionHeading";

type RelatedPartsProps = {
  part: AccessoryPartItem | null;
  priceOption: PricingOption | null;
};

export const RelatedParts: FunctionComponent<RelatedPartsProps> = ({
  part,
  priceOption,
}) => {
  const [language] = useLanguage();
  const { t } = useTranslation();
  const parts = part?.partDetails.relatedParts;
  if (!parts?.length) {
    return null;
  }
  return (
    <Section>
      <SectionHeading translation="searchResults.relatedParts" />
      <div className="grid gap-2 md:grid-cols-2">
        {parts.map(
          ({
            accessoryImageLink,
            accessoryName,
            accessoryPartNumber,
            accessoryRestrictions,
            itemId,
          }) => {
            const relatedPricing = part?.partPricing.relatedParts.find(
              (option) => option.itemId === itemId
            );
            const flatOptions = getFlatPricingOptions(
              relatedPricing || null,
              language
            );
            const price = flatOptions.find(
              ({ context, quantity }) =>
                quantity === priceOption?.quantity &&
                context.pricingContextId ===
                  priceOption?.context.pricingContextId
            );
            return (
              <div
                className="border border-edge rounded-section px-4 py-2 flex gap-3 items-center"
                key={itemId}
              >
                <Image
                  className={clsx(
                    "object-contain block text-black h-20 w-20 rounded-section"
                  )}
                  alt="product image"
                  errorClassName="opacity-60 blur-[4px]"
                  fallbackSrc={getPartFallbackSrc(partTypeAccessory)}
                  src={accessoryImageLink}
                />
                <div>
                  <h3>{accessoryName[language]}</h3>
                  <p className="text-xs">{accessoryRestrictions[language]}</p>
                  <KeyValue className="text-xs">
                    <>{t("searchResults.partNumber")}:</>
                    <>{accessoryPartNumber}</>
                  </KeyValue>
                  <KeyValue className="text-xs">
                    <>{t("searchResults.price")}:</>
                    <PriceDisplay>{price?.context.displayPrice}</PriceDisplay>
                  </KeyValue>
                </div>
              </div>
            );
          }
        )}
      </div>
    </Section>
  );
};
